import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TherapistHeroProps } from '../../types';

// TODO: Integrar React Bites según CLAUDE.md
// import { FadeIn, SlideUp, ScaleIn } from 'react-bites';

/**
 * Componente Hero modernizado siguiendo CLAUDE.md guidelines
 * Sistema de colores: tokens Contexto A (--primary, --secondary, --accent)
 * Animaciones con cubic-bezier(0.23, 1, 0.32, 1)
 * Layout mobile-first con imagen circular y contenido sintetizado
 */
const TherapistHero: React.FC<TherapistHeroProps> = ({ therapist, onBookingClick }) => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleBookingClick = () => {
    if (onBookingClick) {
      onBookingClick();
    } else {
      navigate(therapist.bookingUrl || '/agendar');
    }
  };

  const handleWhatsAppClick = () => {
    const message = `Hola, me gustaría agendar una consulta con ${therapist.name} en Conectemos Chile`;
    // Usar el número de WhatsApp específico del terapista si existe, sino usar el número general
    const whatsappNumber = therapist.socialMedia?.whatsapp || '+56978009119';
    const cleanNumber = whatsappNumber.replace(/\D/g, ''); // Eliminar caracteres no numéricos
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  // Sintetizar descripción para mobile
  const getShortDescription = () => {
    const sentences = therapist.description.split('. ');
    return sentences.slice(0, 2).join('. ') + (sentences.length > 2 ? '.' : '');
  };

  // Obtener especialidades principales
  const getMainSpecialties = () => {
    return therapist.specialties.slice(0, 2);
  };

  return (
    <section className={`therapist-hero-modern ${isVisible ? 'visible' : ''}`}>
      {/* Background sutil */}
      <div className="hero-background-modern">
        <div className="bg-gradient"></div>
        <div className="bg-pattern"></div>
      </div>

      <div className="container">
        <div className="hero-content-modern">
          {/* Imagen circular prominente */}
          <div className="hero-avatar-section">
            <div className="avatar-container-modern">
              <div className="avatar-ring"></div>
              <img
                src={therapist.imageSmall || therapist.image}
                alt={`${therapist.name} - Psicólogo`}
                className="avatar-image-modern"
                loading="eager"
              />
              {/* Badge de disponibilidad flotante */}
              <div className="availability-badge-modern">
                <div className="badge-pulse"></div>
                <i className="fas fa-circle"></i>
              </div>
            </div>
            
            {/* Certificación compacta */}
            <div className="certification-mini">
              <i className="fas fa-award"></i>
              <span>Colegio de Psicólogos</span>
            </div>
            
            {/* Modalidades disponibles - debajo de certificación */}
            <div className="modalities-preview">
              <div className="modalities-label">Modalidades:</div>
              <div className="modalities-list">
                {therapist.availability.modalidades.map((modality, index) => (
                  <span key={index} className="modality-tag">
                    <i className={
                      modality === 'presencial' ? 'fas fa-building' :
                      modality === 'online' ? 'fas fa-video' :
                      'fas fa-sync-alt'
                    }></i>
                    {modality === 'presencial' ? 'Presencial' :
                     modality === 'online' ? 'Online' :
                     'Híbrido'}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Información principal condensada */}
          <div className="hero-info-modern">
            <div className="name-section">
              <h1 className="therapist-name-modern">{therapist.name}</h1>
              <h2 className="therapist-title-modern">{therapist.title}</h2>
            </div>

            {/* Descripción sintetizada */}
            <div className="description-modern">
              <p>{getShortDescription()}</p>
            </div>

            {/* Especialidades destacadas compactas */}
            <div className="specialties-compact">
              {getMainSpecialties().map((specialty, index) => (
                <span key={index} className="specialty-chip">
                  <i className="fas fa-star"></i>
                  {specialty}
                </span>
              ))}
              {therapist.specialties.length > 2 && (
                <span className="specialty-chip more">
                  +{therapist.specialties.length - 2} más
                </span>
              )}
            </div>

            {/* Call to Actions optimizados */}
            <div className="cta-section-modern">
              <button
                onClick={handleBookingClick}
                className="cta-primary-modern"
                aria-label={`Agendar cita con ${therapist.name}`}
              >
                <div className="btn-content">
                  <i className="fas fa-calendar-plus"></i>
                  <span>Agendar Consulta</span>
                </div>
                <div className="btn-glow"></div>
              </button>

              <button
                onClick={handleWhatsAppClick}
                className="cta-secondary-modern"
                aria-label={`Contactar por WhatsApp`}
              >
                <div className="btn-content">
                  <i className="fab fa-whatsapp"></i>
                  <span>WhatsApp</span>
                </div>
              </button>
            </div>
          </div>

        </div>

      </div>

    </section>
  );
};

export default TherapistHero;